import React, { forwardRef } from 'react';

import { QUERIES, BREAKPOINTS } from '@repo/ui/queries';
import take from 'lodash/take';
import styled, { css } from 'styled-components';

export type BreakpointKeys = keyof typeof QUERIES;

type HideProps = Pick<React.CSSProperties, 'display' | 'width' | 'height'> & {
  mobileBreakpoint?: BreakpointKeys;
};

const getQuery = ({ mobileBreakpoint }: HideProps): string =>
  mobileBreakpoint && mobileBreakpoint in QUERIES
    ? QUERIES[mobileBreakpoint]
    : QUERIES.largeTabletAndUp;

const hide = css<HideProps>`
  display: ${({ display }): React.CSSProperties['display'] =>
    display ?? 'block'};
  width: ${({ width }): React.CSSProperties['width'] => width};
  height: ${({ height }): React.CSSProperties['height'] => height};
`;

export const HideMobile = styled.div<HideProps>`
  display: none;

  ${getQuery} {
    ${hide};
  }
`;

export const HideDesktop = styled.div<HideProps>`
  ${hide};

  ${getQuery} {
    display: none;
  }
`;

export type LayoutSwitchWrapperProps = Pick<
  React.CSSProperties,
  'position' | 'width' | 'height'
>;

const LayoutSwitchWrapper = styled.div<LayoutSwitchWrapperProps>`
  position: ${({ position }): React.CSSProperties['position'] =>
    position ?? 'unset'};
  left: ${({ position }): React.CSSProperties['left'] =>
    position ? 0 : 'unset'};
  top: ${({ position }): React.CSSProperties['top'] =>
    position ? 0 : 'unset'};
  width: ${({ width }): React.CSSProperties['width'] => width};
  height: ${({ height }): React.CSSProperties['height'] => height};
`;

export interface LayoutSwitchBreakpointWrapperProps
  extends Pick<React.CSSProperties, 'width' | 'height'> {
  breakpointBefore?: BREAKPOINTS;
  breakpointAfter?: BREAKPOINTS;
}

const LayoutSwitchBreakpointWrapper = styled.div<LayoutSwitchBreakpointWrapperProps>`
  width: ${({ width }): React.CSSProperties['width'] => width};
  height: ${({ height }): React.CSSProperties['height'] => height};
  ${({ breakpointBefore }) =>
    breakpointBefore
      ? `
    @media (min-width: ${breakpointBefore}px) {
      display: none;
    }
  `
      : undefined}

  ${({ breakpointAfter }) =>
    breakpointAfter
      ? `
    @media (max-width: ${breakpointAfter - 1}px) {
      display: none;
    }
  `
      : undefined}
`;

interface Variation {
  props: Record<string, unknown>;
  width?: string;
  height?: string;
}

interface LayoutSwitchProps extends LayoutSwitchWrapperProps {
  /**
   * @deprecated use breakpoints and variationProps instead
   */
  desktopProps?: Record<string, unknown>;
  /**
   * @deprecated use breakpoints and variationProps instead
   */
  mobileProps?: Record<string, unknown>;
  /**
   * @deprecated use breakpoints and variationProps instead
   */
  desktopWidth?: string;
  /**
   * @deprecated use breakpoints and variationProps instead
   */
  desktopHeight?: string;
  /**
   * @deprecated use breakpoints and variationProps instead
   */
  mobileWidth?: string;
  /**
   * @deprecated use breakpoints and variationProps instead
   */
  mobileHeight?: string;
  children: React.ReactElement;
  /**
   * @deprecated use breakpoints and variationProps instead
   */
  mobileBreakpoint?: HideProps['mobileBreakpoint'];

  /**
   * breakpoints list from the biggest screen size to lowest
   */
  breakpoints?: number[];
  /**
   * variations from here will be placed around breakpoints
   * ( +∞; breakpoint[0] ] - variation[0]
   * ( breakpoint[0]; breakpoint[1] ] - variation[1]
   * ...
   * ( breakpoint[n-1]; breakpoint[n] ] - variation[n-1]
   * ( breakpoint[n]; 0 ] - variation[n]
   */
  variationProps?: Variation[];
}

export const LayoutSwitch = forwardRef<HTMLDivElement, LayoutSwitchProps>(
  (props, ref) => {
    const {
      desktopProps,
      mobileProps,
      mobileWidth,
      mobileHeight,
      desktopWidth,
      desktopHeight,
      mobileBreakpoint,
      breakpoints,
      variationProps,
    } = props;

    if (!breakpoints || !variationProps) {
      // keep the old one because breakpoints are not easily convertable
      return (
        <LayoutSwitchWrapper {...props} ref={ref}>
          <HideDesktop
            height={mobileHeight}
            mobileBreakpoint={mobileBreakpoint}
            width={mobileWidth}
          >
            {React.Children.map(props.children, (child) =>
              child
                ? React.cloneElement(child, { ...child.props, ...mobileProps })
                : null
            )}
          </HideDesktop>
          <HideMobile
            height={desktopHeight}
            mobileBreakpoint={mobileBreakpoint}
            width={desktopWidth}
          >
            {React.Children.map(props.children, (child) =>
              child
                ? React.cloneElement(child, { ...child.props, ...desktopProps })
                : null
            )}
          </HideMobile>
        </LayoutSwitchWrapper>
      );
    }

    return (
      <LayoutSwitchWrapper {...props} ref={ref}>
        {take(variationProps, breakpoints.length + 1).map(
          (variation: Variation, index: number) => (
            <LayoutSwitchBreakpointWrapper
              key={index}
              breakpointAfter={breakpoints[index]}
              breakpointBefore={breakpoints[index - 1]}
              height={variation.height}
              width={variation.width}
            >
              {React.Children.map(props.children, (child) =>
                child
                  ? React.cloneElement(child, {
                      ...child.props,
                      ...variation.props,
                    })
                  : null
              )}
            </LayoutSwitchBreakpointWrapper>
          )
        )}
      </LayoutSwitchWrapper>
    );
  }
);

LayoutSwitch.displayName = 'LayoutSwitch';

interface LayoutVariationProps extends LayoutSwitchWrapperProps {
  desktopWidth?: string;
  desktopHeight?: string;
  mobileWidth?: string;
  mobileHeight?: string;
  mobileElement: React.ReactElement;
  desktopElement: React.ReactElement;
  breakpoint: HideProps['mobileBreakpoint'];
}

export const LayoutVariation = forwardRef<HTMLDivElement, LayoutVariationProps>(
  (props, ref) => {
    return (
      <LayoutSwitchWrapper {...props} ref={ref}>
        <HideDesktop
          height={props.mobileHeight}
          mobileBreakpoint={props.breakpoint}
          width={props.mobileWidth}
        >
          {props.mobileElement}
        </HideDesktop>
        <HideMobile
          height={props.desktopHeight}
          mobileBreakpoint={props.breakpoint}
          width={props.desktopWidth}
        >
          {props.desktopElement}
        </HideMobile>
      </LayoutSwitchWrapper>
    );
  }
);

LayoutVariation.displayName = 'LayoutVariation';
